import moment from 'moment';

import DepsService from 'services/dependencies';
import _ from 'lodash';

const CONTEXT = 'dictionaries';
const INVOICE_DATE_FORMAT = 'YYYY-MM-DD';

// Helpers
export const lowerCase = (string) => {
  if (!string) return null;

  return _.lowerCase(string);
};

export const transformTemplatesDTO = (templates) => {
  let dictionaryDTO = {};

  templates.forEach((template) => {
    dictionaryDTO = {
      ...dictionaryDTO,
      [template.id]: {
        ...template.translations,
      },
    };
  });

  return dictionaryDTO;
};

export const transformDictionaryDTO = (data) => {
  let dictionaryDTO = {};

  data.forEach((item) => {
    const key = item.stage || item.typeCode || item.key;
    dictionaryDTO = {
      ...dictionaryDTO,
      [key]: {
        ...item,
      },
    };
  });

  return dictionaryDTO;
};

export const calculateRange = (date) => {
  const startDate = DepsService.$filter('date')(date.periodStartDate, 'shortDate');
  const endDate = DepsService.$filter('date')(date.periodEndDate, 'shortDate');

  return `${startDate} - ${endDate}`;
};

export const calculateInvoicesDatesRange = (dates) =>
  dates.map((date) => ({
    id: moment(date.periodEndDate).format(INVOICE_DATE_FORMAT),
    value: calculateRange(date),
  }));

export const transformInvoiceDates = (dates) => {
  const DATE_FORMAT = 'YYYY-MM-DD';
  let transformedDates = {};

  dates.forEach((date) => {
    const fromDate = moment(date).subtract(6, 'd').format(DATE_FORMAT);
    const toDate = moment(date).format(DATE_FORMAT);

    transformedDates = {
      ...transformedDates,
      [toDate]: `${fromDate} - ${toDate}`,
    };
  });

  return transformedDates;
};

export const transformOutletTypesDTO = (data) => {
  let dictionaryDTO = {};

  data.forEach((item) => {
    dictionaryDTO = {
      ...dictionaryDTO,
      [item.code]: item.displayName,
    };
  });

  return dictionaryDTO;
};

export const transformActivityEvents = (events) => _.groupBy(events, 'category');

export const transformLotteryTermsDTO = (data) => {
  let dictionaryDTO = {};

  data.forEach(({ locale, key, ...term }) => {
    dictionaryDTO = {
      ...dictionaryDTO,
      [locale]: {
        ...dictionaryDTO[locale],
        [key]: {
          ...term,
          pluralLowerCase: lowerCase(term.plural),
          singularLowerCase: lowerCase(term.singular),
        },
      },
    };
  });

  return dictionaryDTO;
};

export const fetchPricePointsDictionary = async ({ locale }) => {
  const urlParams = {};
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.pricePoints;

  const response = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformDictionaryDTO(response);
};

export const fetchPackStatusesDictionary = async ({ locale }) => {
  const urlParams = {};
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.packStatuses;

  const response = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformDictionaryDTO(response);
};

export const fetchOrderStatusesDictionary = async ({ locale }) => {
  const urlParams = {};
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.orderStatuses;

  const response = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformDictionaryDTO(response);
};

export const fetchUserClassDictionary = async ({ locale }) => {
  const urlParams = { name: 'user-class' };
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.names;

  const result = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return result.values;
};

export const fetchReportCategoriesDictionary = async ({ locale }) => {
  const urlParams = { name: 'report-category' };
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.names;

  const result = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return result.values;
};

export const fetchTiersDictionary = async (locale) => {
  const { url } = DepsService.rest.dashboard.cumulative.tierTypes;
  const queryParams = { locale };
  const urlParams = {};

  return await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);
};

export const fetchInquiryReasonsDictionary = async (locale) => {
  const { url } = DepsService.rest.contact.inquiryReasons;
  const queryParams = { locale };
  const urlParams = {};
  const path = 'contact.inquiryReasons';

  return DepsService.restSrv.list(CONTEXT, url, queryParams, urlParams, path);
};

export const fetchInvoiceDatesDictionary = async (retailerId) => {
  const { url } = DepsService.rest.dashboard.cumulative.invoiceDates;
  const queryParams = { retailerId };
  const urlParams = {};

  const dates = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return calculateInvoicesDatesRange(dates);
};

export const fetchActivityEventsDictionary = async (locale) => {
  const urlParams = { name: 'activity-report-type' };
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.names;

  const events = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformActivityEvents(events);
};

export const fetchUserTemplatesDictionary = async (viewType) => {
  let url;
  switch (viewType) {
    case 'all':
      url = DepsService.rest.account.templates.url;
      break;
    case 'lotteryView':
      url = DepsService.rest.privileges.ADMIN.templates.url;
      break;
    case 'RETAILER_PRIMARY':
      url = DepsService.rest.privileges.RETAILER_PRIMARY.templates.url;
      break;
    case 'RETAILER_SECONDARY':
    default:
      url = DepsService.rest.privileges.RETAILER_SECONDARY.templates.url;
      break;
  }

  const urlParams = {};
  const queryParams = {};

  const templates = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformTemplatesDTO(templates);
};

export const fetchOutletTypesDictionary = async (locale) => {
  const urlParams = {};
  const queryParams = { locale };

  const { url } = DepsService.rest.dictionary.outletTypes;

  const outletTypes = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformOutletTypesDTO(outletTypes);
};

export const fetchSftpAuthenticationTypesDictionary = async () => {
  const urlParams = { name: 'authentication-types' };
  const queryParams = {};

  const { url } = DepsService.rest.dictionary.names;

  const result = await DepsService.restSrv.listCached(CONTEXT, url, queryParams, urlParams);

  return result.values;
};

export const fetchLotteryTermsDictionary = async () => {
  const { url } = DepsService.rest.dictionary.lotteryTerms;
  const queryParams = {};
  const urlParams = {};

  const terms = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformLotteryTermsDTO(terms);
};

export const fetchPrivilegeDisplayTypesDictionary = async (locale) => {
  const urlParams = { locale };
  const queryParams = {};

  const { url } = DepsService.rest.privileges.displayTypes;

  const response = await DepsService.restSrv.get(CONTEXT, url, queryParams, urlParams);

  return transformDictionaryDTO(response);
};



// WEBPACK FOOTER //
// ./src/modules/app/resources/dictionaries.api.js